
import React, { forwardRef, useId } from "react";
import { Field } from "./Field";

interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  error?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightSlot?: React.ReactNode;
  fieldClassName?: string;
  /** Alias für fieldClassName (Legacy-Kompatibilität) — Klassen des äußeren Wrappers. */
  containerClassName?: string;
  /** bordered (Default) oder underline (nur Unterkante). */
  variant?: "bordered" | "underline";
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    helperText,
    error,
    leftIcon,
    rightSlot,
    id,
    required,
    className = "",
    fieldClassName,
    containerClassName,
    variant = "bordered",
    ...rest
  },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hasError = !!error;
  const wrapperClasses = ["ds-input-wrap"];
  if (leftIcon) wrapperClasses.push("has-prefix");
  if (rightSlot) wrapperClasses.push("has-suffix");
  const fieldCls = [fieldClassName, containerClassName].filter(Boolean).join(" ") || undefined;
  const inputCls = `ds-input ${variant === "underline" ? "ds-input--underline" : ""} ${className}`
    .replace(/\s+/g, " ")
    .trim();

  return (
    <Field
      label={label}
      helperText={helperText}
      error={error}
      htmlFor={inputId}
      required={required}
      className={fieldCls}
    >
      <div className={wrapperClasses.join(" ")}>
        {leftIcon && <span className="ds-input-prefix">{leftIcon}</span>}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-invalid={hasError || undefined}
          className={inputCls}
          {...rest}
        />
        {rightSlot && <span className="ds-input-suffix">{rightSlot}</span>}
      </div>
    </Field>
  );
});
